import * as React from 'react';
import { currentUrl, registerComponent, replaceTranslationArgs, Translation, useMatchMobile } from '@avensia/excite';
import { styled } from '@glitz/react';
import {
  Block,
  desktopEdgeMarginDecorator,
  edgeMarginsDecorator,
  maxContentNormalWidthDecorator,
} from 'webapp/components/page-layout';
import { createMinLargeViewportDecorator, createMaxSmallViewportDecorator } from 'webapp/viewport';
import { ContentList } from '@avensia/excite-ninetailed';
import { ArticleListing } from 'webapp/inspiration/article-listing';
import { TestIdProps } from '@avensia/test-automation';
import { searchUrl } from 'webapp/search/utils';
import { BrandListing } from 'webapp/brand-listing';
import { CollectionListing } from 'webapp/collection-listing';
import { Tabs } from 'webapp/components/Tabs';
import Link from 'webapp/components/Link';
import ProductListing from 'webapp/product-listing';
import { EditorialListing } from 'webapp/editorial-listing';
import { DesignerListing } from 'webapp/designer-listing';
import { ProductListingSearchListing } from 'webapp/product-listing-seach';
import { useRetailMedia } from 'webapp/components/retail-media/use-retail-media';
import RetailMediaBannerAd from 'webapp/components/retail-media/RetailMediaBannerAd';

export default registerComponent<NordicNest.Api.SearchPageApiModel>(props => {
  const selectedRangedFacets =
    props.facets?.filter(
      facet =>
        facet.isRange && (facet.range.maxSelected !== facet.range.max || facet.range.minSelected !== facet.range.min),
    ) || [];

  const retailMediaContent = useRetailMedia(
    'search',
    undefined,
    props.searchPhrase ?? '',
    undefined,
    [...props.selectedFacets, ...selectedRangedFacets],
    props.retailMediaEnabled,
  );

  const isMobile = useMatchMobile();
  const topRetailMediaBanner = isMobile
    ? retailMediaContent?.mobileTopBanners[0] || retailMediaContent?.topBanners[0]
    : retailMediaContent?.topBanners[0];

  const totalBrandItems = props.totalBrandHits;
  const totalCollectionItems = props.totalCollectionHits;
  const totalArticleItems = props.articlePageCount;
  const totalProductListingItems = props.totalProductListingHits;
  const totalEditorialItems = props.totalEditorialHits;
  const totalDesignerItems = props.totalDesignerHits;

  const productTab =
    props.pagination?.total || props.selectedFacets?.length
      ? replaceTranslationArgs(props.page.productSearchResultTab, { ['count']: props.pagination.total })
      : null;
  const brandTab = totalBrandItems
    ? replaceTranslationArgs(props.page.brandPageSearchResultTab, {
        ['count']: totalBrandItems,
      })
    : null;
  const collectionTab = totalCollectionItems
    ? replaceTranslationArgs(props.page.collectionPageSearchResultTab, {
        ['count']: totalCollectionItems,
      })
    : null;
  const articleTab = totalArticleItems
    ? replaceTranslationArgs(props.page.articlePageSearchResultTab, {
        ['count']: totalArticleItems,
      })
    : null;
  const productListingTab = totalProductListingItems
    ? replaceTranslationArgs(props.page.productListingSearchResultTab, {
        ['count']: totalProductListingItems,
      })
    : null;
  const designerListingTab = totalDesignerItems
    ? replaceTranslationArgs(props.page.designerPageSearchResultTab, {
        ['count']: totalDesignerItems,
      })
    : null;
  const editorialListingTab = totalEditorialItems
    ? replaceTranslationArgs(props.page.editorialPageSearchResultTab, {
        ['count']: totalEditorialItems,
      })
    : null;

  const totalHits = props.totalSearchHits;

  const tabHeaderTexts = [
    productTab,
    brandTab,
    collectionTab,
    articleTab,
    productListingTab,
    designerListingTab,
    editorialListingTab,
  ].filter(t => t !== null) as string[];
  const nbrOfSuggestions = props.didYouMeanSuggestions?.length ?? 0;
  return (
    <Base>
      <TopContentContainer>
        <TopContent>
          {props.originalSearch === props.searchPhrase ? (
            <SearchText {...TestIdProps.SearchResultText}>
              <Translation
                text={props.page.searchResultLabel}
                args={{
                  ['search_phrase']: <b key={props.originalSearch}>{props.originalSearch}</b>,
                  ['total_search_hits']: totalHits,
                }}
              />
            </SearchText>
          ) : (
            <>
              <SearchText>
                <Translation
                  text={props.page.searchResultLabel}
                  args={{
                    ['search_phrase']: <b key={props.searchPhrase}>{props.searchPhrase}</b>,
                    ['total_search_hits']: totalHits,
                  }}
                />
              </SearchText>
              <DidYouMean>
                <Translation
                  text={props.page.suggestedSearchResultLabel}
                  args={{
                    ['search_phrase']: props.originalSearch,
                    ['suggest_search_phrase']: props.searchPhrase,
                  }}
                />
                {nbrOfSuggestions > 0 && (
                  <>
                    <DidYouMeanText>{props.page.didYouMeanSuggestionsLabel}</DidYouMeanText>
                    {props.didYouMeanSuggestions.map((suggestion, index) => (
                      <React.Fragment key={suggestion}>
                        {nbrOfSuggestions > 1 && index === nbrOfSuggestions - 1 && props.page.suggestionSeparatorLabel}
                        <DidYouMeanLink
                          to={searchUrl(
                            currentUrl().pathname,
                            suggestion,
                            true,
                            true,
                            true,
                            suggestion,
                            currentUrl().toString(),
                          )}
                        >
                          {suggestion}
                        </DidYouMeanLink>
                        {index !== nbrOfSuggestions - 1 && index !== nbrOfSuggestions - 2 && ', '}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </DidYouMean>
            </>
          )}
        </TopContent>
      </TopContentContainer>

      {topRetailMediaBanner !== undefined && <TopRetailMediaBannerAd banner={topRetailMediaBanner} />}

      <Tabs tabHeaders={tabHeaderTexts}>
        {((props.pagination?.total ?? 0) > 0 || props.selectedFacets?.length) && (
          <ProductListing
            id={'search_result_' + props.searchPhrase}
            name={'search_result'}
            selectedFacets={props.selectedFacets}
            products={props.products}
            facets={props.facets}
            pagination={props.pagination}
            sortOptions={props.sortOptions}
            retailMediaBannerAd={retailMediaContent?.productListBanners[0]}
            retailMediaSponsoredProducts={retailMediaContent?.sponsoredProducts}
          />
        )}
        {totalBrandItems > 0 && <BrandPageListing list={props.brands} />}
        {totalCollectionItems > 0 && <CollectionPageListing list={props.collections} />}
        {totalArticleItems > 0 && <ArticlePageListing list={props.articlePages} />}
        {totalProductListingItems > 0 && <PlpPageListing list={props.productListingPages} />}
        {totalDesignerItems > 0 && <DesignerPageListing list={props.designerPages} />}
        {totalEditorialItems > 0 && <EditorialPageListing list={props.editorialPages} />}
      </Tabs>

      {totalHits === 0 && props.page.content?.items && (
        <EmptyContent>
          <ContentList items={props.page.content.items} />
        </EmptyContent>
      )}
    </Base>
  );
});

const Base = styled.div();

const TopContentContainer = styled.div();

const ArticlePageListing = styled(ArticleListing, edgeMarginsDecorator, {
  padding: { y: t => t.spacing.xl },
});

const BrandPageListing = styled(BrandListing, desktopEdgeMarginDecorator, {
  padding: { y: t => t.spacing.xl },
});

const CollectionPageListing = styled(CollectionListing, desktopEdgeMarginDecorator, {
  padding: { y: t => t.spacing.xl },
});

const PlpPageListing = styled(ProductListingSearchListing, desktopEdgeMarginDecorator, {
  padding: { y: t => t.spacing.xl },
});

const EditorialPageListing = styled(EditorialListing, desktopEdgeMarginDecorator, {
  padding: { y: t => t.spacing.xl },
});

const DesignerPageListing = styled(DesignerListing, desktopEdgeMarginDecorator, {
  padding: { y: t => t.spacing.xl },
});

const TopContent = styled(
  Block,
  edgeMarginsDecorator,
  maxContentNormalWidthDecorator,
  createMinLargeViewportDecorator({
    padding: { y: t => t.sharedSpacing.xxl },
  }),
  createMaxSmallViewportDecorator({
    margin: { y: t => t.sharedSpacing.l },
  }),
);

const EmptyContent = styled.div(
  maxContentNormalWidthDecorator,
  edgeMarginsDecorator,
  createMinLargeViewportDecorator({
    padding: { y: t => t.sharedSpacing.xxxxl },
  }),
  createMaxSmallViewportDecorator({
    margin: { y: t => t.sharedSpacing.xxl },
  }),
);

const SearchText = styled.h1(
  createMaxSmallViewportDecorator({
    fontSize: t => t.fontSize.m,
    marginBottom: t => t.sharedSpacing.s,
  }),
  {
    textAlign: 'center',
    fontSize: t => t.fontSize.xl,
    marginBottom: t => t.sharedSpacing.l,
  },
);

const DidYouMean = styled.div(createMaxSmallViewportDecorator({ fontSize: t => t.fontSize.xxs }), {
  textAlign: 'center',
  fontSize: t => t.fontSize.xs,
});

const DidYouMeanText = styled.span({
  marginRight: t => t.spacing.xxs,
});

const DidYouMeanLink = styled(Link, {
  fontWeight: 'bold',
  textDecoration: 'underline',
  ':hover': {
    textDecoration: 'underline',
  },
});

const TopRetailMediaBannerAd = styled(RetailMediaBannerAd, {
  marginBottom: t => t.spacing.l,
});
